import { DestroyRef, Directive, NgZone, effect, inject, input, signal } from '@angular/core'

/** Owns temporary press feedback for every row in the shared list. */
@Directive({
  selector: '[awMobileListItem]',
  host: {
    '[class.mobile-list-row--pressed]': 'pressed()',
    '(pointerdown)': 'startPress($event)',
    '(keydown)': 'keyPress($event)',
    '(keyup)': 'endPress()',
    '(focusout)': 'endPress()'
  }
})
export class MobileListItemDirective {
  private readonly zone = inject(NgZone)
  readonly awMobileListItem = input.required<string>()
  readonly pressDisabled = input(false)
  protected readonly pressed = signal(false)
  private pointer?: { id: number; x: number; y: number }
  private stopListening?: () => void

  constructor() {
    inject(DestroyRef).onDestroy(() => this.endPress())
    effect(() => {
      this.awMobileListItem()
      this.pressDisabled()
      this.endPress()
    })
  }

  /**
   * Starts feedback for a primary pointer on an enabled control.
   * @param {PointerEvent} event Pointer that started the press.
   */
  protected startPress(event: PointerEvent): void {
    if (this.pressDisabled() || event.button !== 0 || !event.isPrimary || this.isIgnoredTarget(event.target)) {
      return
    }

    this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY }
    this.pressed.set(true)
    this.listenWhilePressed()
  }

  /**
   * Clears feedback when a tap turns into scrolling or dragging.
   * @param {PointerEvent} event Current pointer position.
   */
  protected movePress(event: PointerEvent): void {
    if (
      this.pointer?.id === event.pointerId &&
      Math.hypot(event.clientX - this.pointer.x, event.clientY - this.pointer.y) >= 8
    ) {
      this.endPress()
    }
  }

  /**
   * Shows the same feedback while activating a row with the keyboard.
   * @param {KeyboardEvent} event Activation key.
   */
  protected keyPress(event: KeyboardEvent): void {
    if (!this.pressDisabled() && !this.isIgnoredTarget(event.target) && (event.key === 'Enter' || event.key === ' ')) {
      this.pressed.set(true)
      this.listenWhilePressed()
    }
  }

  /** Restores the resting state after release, cancellation, or view reuse. */
  protected endPress(): void {
    this.stopListening?.()
    this.stopListening = undefined
    this.pointer = undefined
    this.pressed.set(false)
  }

  /** Watches the active press outside Angular and removes global listeners when it ends. */
  private listenWhilePressed(): void {
    if (this.stopListening) {
      return
    }

    const move = (event: PointerEvent): void => this.movePress(event)
    const release = (event: PointerEvent): void => {
      if (this.pointer?.id === event.pointerId) {
        this.endPress()
      }
    }
    const blur = (): void => this.endPress()

    this.zone.runOutsideAngular(() => {
      window.addEventListener('pointermove', move, { passive: true })
      window.addEventListener('pointerup', release, { passive: true })
      window.addEventListener('pointercancel', release, { passive: true })
      window.addEventListener('blur', blur)
    })
    this.stopListening = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', release)
      window.removeEventListener('pointercancel', release)
      window.removeEventListener('blur', blur)
    }
  }

  /**
   * Leaves editing fields, disabled controls, and swipe buttons to their own feedback.
   * @param {EventTarget | null} target Element being activated.
   * @returns {boolean} Whether the row should skip press feedback.
   */
  private isIgnoredTarget(target: EventTarget | null): boolean {
    return (
      target instanceof Element &&
      !!target.closest(
        'input, textarea, select, [contenteditable="true"], :disabled, [aria-disabled="true"], .swipe-row__actions'
      )
    )
  }
}
